import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { RecordData, Service, SourceInfo } from 'webapp-types';
import { ModelName, type TemplateObjectType as ObjectType } from 'webapp/enums/enums';
import type { ValidationRule } from 'webapp/utils/validate';

import Board from 'webapp/models-custom/board';
import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import type TemplateRecord from 'webapp/models/template';

export interface Creator {
	name: string | null;
	url: string | null;
	avatar: string | null;
}

export interface TemplateMetricKey {
	// dsType: number; // still returned, but unused (but needed in website)
	// keyLong: string; // still returned, but unused (but needed in website)
	name: string;
}

export default class Template extends BufferedModel<
	TemplateRecord,
	RecordData.Template.Serialized
> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked override animationClass = ''; // Used for animations in lazy-loading

	// ------- props from / to ED record ------

	get id(): string | null {
		return this.model.id;
	}

	get title(): string {
		return this.getBuffered('title');
	}

	set title(value: string) {
		this.setBuffered('title', value);
	}

	get categories(): number[] {
		return this.getBuffered('categories');
	}

	set categories(value: number[]) {
		this.setBuffered('categories', value);
	}

	get createdBy(): Creator {
		return this.getBuffered('createdBy');
	}

	set createdBy(value: Creator) {
		this.setBuffered('createdBy', value);
	}

	get description(): string {
		return this.getBuffered('description');
	}

	set description(value: string) {
		this.setBuffered('description', value);
	}

	get objectId(): number {
		return this.getBuffered('objectId');
	}

	set objectId(value: number) {
		this.setBuffered('objectId', value);
	}

	get objectType(): ObjectType {
		return this.getBuffered('objectType');
	}

	set objectType(value: ObjectType) {
		this.setBuffered('objectType', value);
	}

	get token(): string {
		return this.model.token;
	}

	get metricKeys(): TemplateMetricKey[] {
		return this.model.metricKeys;
	}

	get images(): string[] {
		return this.model.images;
	}

	get sources(): number[] {
		return this.model.sources;
	}

	get usedCount(): number {
		return this.model.usedCount;
	}

	get keywords(): string {
		return this.getBuffered('keywords');
	}

	set keywords(value: string) {
		this.setBuffered('keywords', value);
	}

	get boardIds(): number[] {
		return this.getBuffered('boardIds');
	}

	set boardIds(value: number[]) {
		this.setBuffered('boardIds', value);
	}

	get isPublic(): boolean {
		return this.getBuffered('isPublic');
	}

	set isPublic(value: boolean) {
		this.setBuffered('isPublic', value);
	}

	override get validationRules(): Record<string, ValidationRule[]> {
		return {
			title: ['required'],
			categories: ['required'],
			objectId: ['required'],
		};
	}

	get descriptionText(): string {
		return (this.description || '').replace(/\r?\n/g, '<br />');
	}

	get createdByText(): string {
		const { name, url } = this.createdBy || {};
		let text = `<span>${name}</span>` || '';

		if (url) {
			text = `<a href="${url}" target="_blank">${text}</a>`;
		}

		return text;
	}

	get specificSourceTypesKeys(): string[] {
		const specificSourceTypesKeys: string[] = [];

		this.mmSourceTypesService.sourceTypeModels.forEach((sourceType) => {
			if (sourceType.id && this.sources.includes(sourceType.id)) {
				specificSourceTypesKeys.push(sourceType.key);
			}
		});

		return specificSourceTypesKeys;
	}

	get hasMergedDatasetMetrics(): boolean {
		return this.model.hasMergedDatasetMetrics ?? false;
	}

	get sourcesUsedList() {
		const sourcesUsed: SourceInfo[] = [];

		this.mmSourceTypesService.sourceTypeModels.forEach((sourceType) => {
			if (sourceType.id && this.sources.includes(sourceType.id)) {
				sourcesUsed.push({
					value: sourceType.id,
					label: sourceType.name,
					avatar: sourceType.avatar,
				});
			}
		});

		if (this.hasMergedDatasetMetrics) {
			sourcesUsed.push({
				value: -1,
				label: 'Merged dataset',
				avatar: 'https://cdn1.databox.com/images/apps/s/Merged.png',
			});
		}

		return sourcesUsed;
	}

	@cached
	get metricsNames(): string {
		return this.metricKeys.map((m) => m.name).join(' ');
	}

	get image(): string {
		return this.images[0] || '';
	}

	get imagesMedium(): string[] {
		return this.images.map((image) => {
			return Board.snapshotResize(image, 'm');
		});
	}

	get imageMedium(): string {
		return this.imagesMedium[0] || '';
	}

	get imagesLarge(): string[] {
		return this.images.map((image) => {
			return Board.snapshotResize(image, 'l');
		});
	}

	get imageLarge(): string {
		return this.imagesLarge[0] || '';
	}

	static override modelName = ModelName.TEMPLATE; // store record type
}
